import { Alert } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'

import { CRM_VERIFICATION_TEXTS as T } from './CrmVerification'
import { verificationOwnerKey, verificationRequirements } from './VerificationRequirementsStore'

/** Яка 422 уже лягла на яку форму — щоб не ставити ті самі помилки вдруге. */
const applied = new WeakMap<object, readonly unknown[]>()

/** Те, що компонент бере з ядрового `formContext` картки власника. */
export interface VerificationRequirementsFormLike {
    isEditable?: boolean
    values?: { uuid?: unknown; [key: string]: unknown }
    errors?: Record<string, string>
    getError?: (path: string) => unknown
    setErrors?: (errors: Record<string, string>) => void
}

export interface VerificationRequirementsAlertProps {
    formContext: VerificationRequirementsFormLike
    /** Колекція власника — частина ключа запису (`/services/clients`) */
    restUrl: string
    /** Підписи полів картки: у переліку — назва поля, а не текст помилки */
    fieldLabels: Readonly<Record<string, string>>
}

/**
 * Перелік незаповнених вимог верифікації в картці, що перейшла в
 * редагування після 422 (`SPEC-008` §5.4.2, `SPEC-009` §8.6, КЛ-17).
 *
 * Робить дві речі:
 *
 *  1. **ставить помилки полям** — ті самі тексти бека під кожним із (до
 *     13) полів; у «Вартість + валюта» — одна під обома (`CostCurrencyField`);
 *  2. **показує, чого бракує**, — назвами полів, а не текстами помилок:
 *     частина полів лежить нижче, у блоці «Верифікація», і з першого екрана
 *     їх не видно.
 *
 * Перелік живий: він показує лише поля, під якими помилка ще стоїть. Ядрова
 * форма знімає помилки на «Зберегти», тож після збереження блок зникає сам.
 *
 * ⚠️ Окремий observer: він читає помилки форми, а шаблон картки значень і
 * помилок не читає (`SPEC-002` §4.2). Малюється лише в редагуванні.
 */
export const VerificationRequirementsAlert = observer(
    ({ formContext, restUrl, fieldLabels }: VerificationRequirementsAlertProps) => {
        const uuid = formContext.values?.uuid
        const ownerKey = typeof uuid === 'string' && uuid !== '' ? verificationOwnerKey(restUrl, uuid) : null
        const requirements = ownerKey !== null ? verificationRequirements.get(ownerKey) : null
        const isEditable = formContext.isEditable === true

        // Помилки — один раз на форму, щойно картка завантажилася (ядро
        // скидає помилки на початку завантаження, а шаблон малюється вже після
        // нього). Саме «на форму», а не «на монтування»: на «Зберегти» ядро
        // показує замість шаблону скелет, і після збереження шаблон
        // монтується заново — помилки, зняті збереженням, не мають повернутися
        useEffect(() => {
            if (!isEditable || requirements === null || requirements.length === 0) return
            if (applied.get(formContext) === requirements) return

            applied.set(formContext, requirements)
            formContext.setErrors?.({
                ...(formContext.errors ?? {}),
                ...Object.fromEntries(requirements.map(({ field, message }) => [field, message])),
            })
        }, [formContext, isEditable, requirements])

        if (!isEditable || requirements === null) return null

        const missing = requirements.filter(({ field }) => {
            const error = formContext.getError?.(field)

            return typeof error === 'string' && error !== ''
        })

        if (missing.length === 0) return null

        return (
            <Alert
                type="error"
                showIcon
                title={T.fillRequired}
                description={
                    <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                        {missing.map(({ field, message }) => (
                            <li key={field}>{fieldLabels[field] ?? message}</li>
                        ))}
                    </ul>
                }
                style={{ marginBottom: 16 }}
                data-testid="verification-requirements"
            />
        )
    }
)
